import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  output,
  effect,
  signal,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TreeSelectModule } from 'primeng/treeselect';
import { ChipModule } from 'primeng/chip';
import { DocumentationPathService } from '../../services/documentation-path.service';
import { PathAssignment, DocumentationPathTreeNode } from '../../models/documentation-path.model';

@Component({
  selector: 'app-path-selector',
  standalone: true,
  imports: [CommonModule, TreeSelectModule, ChipModule, FormsModule],
  templateUrl: './path-selector.component.html',
  styleUrls: ['./path-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PathSelectorComponent {
  pathService = inject(DocumentationPathService);
  cdr = inject(ChangeDetectorRef);

  // Inputs/Outputs configurados como latest angular specification
  selectedPaths = input<PathAssignment[]>([]);
  primaryPathId = input<string | null>(null);

  pathsChange = output<PathAssignment[]>();
  primaryChange = output<string>();

  _selectedNodes: DocumentationPathTreeNode[] = [];
  treeNodes = signal<DocumentationPathTreeNode[]>([]);

  constructor() {
    this.pathService.loadTree(4).subscribe({
      next: nodes => {
        this.treeNodes.set(nodes.map(n => this.pathService.mapToTreeNode(n)));
        this.cdr.markForCheck();
      },
    });

    // Sync input initial data => control data
    effect(() => {
      const paths = this.selectedPaths();
      const allNodes = this.getAllNodes(this.treeNodes());

      const selectedFormsNodes = paths
        .map(p => allNodes.find(n => n.key === p.pathId))
        .filter((n): n is DocumentationPathTreeNode => !!n);

      this._selectedNodes = selectedFormsNodes;
      this.cdr.markForCheck();
    });
  }

  private getAllNodes(nodes: DocumentationPathTreeNode[]): DocumentationPathTreeNode[] {
    let result: DocumentationPathTreeNode[] = [];
    for (const node of nodes) {
      result.push(node);
      if (node.children) {
        result = result.concat(this.getAllNodes(node.children));
      }
    }
    return result;
  }

  private findNodePath(
    nodes: DocumentationPathTreeNode[],
    targetId: string,
    currentPath: DocumentationPathTreeNode[] = []
  ): DocumentationPathTreeNode[] | null {
    for (const node of nodes) {
      const newPath = [...currentPath, node];
      if (node.key === targetId) {
        return newPath;
      }
      if (node.children && node.children.length > 0) {
        const found = this.findNodePath(node.children, targetId, newPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  onNodeSelectEvent(event: any) {
    const node = event.node;
    if (!node || !node.key) return;

    const newSelection = this.findNodePath(this.treeNodes(), node.key) || [node];
    this._selectedNodes = newSelection;
    this.emitSelection();
  }

  onNodeUnselectEvent(event: any) {
    const node = event.node;
    if (!node || !node.key) return;

    const currentPath = this.findNodePath(this.treeNodes(), node.key) || [];
    const index = currentPath.findIndex(n => n.key === node.key);

    if (index >= 0) {
      this._selectedNodes = currentPath.slice(0, index);
    } else {
      this._selectedNodes = [];
    }
    this.emitSelection();
  }

  onClearEvent() {
    this._selectedNodes = [];
    this.emitSelection();
  }

  emitSelection() {
    const nodes = this._selectedNodes;

    const currentPrimary = this.primaryPathId();

    const assignments: PathAssignment[] = nodes.map((node: any, i: number) => {
      let makePrimary = false;
      if (node.key === currentPrimary) makePrimary = true;

      return {
        pathId: node.key,
        isPrimary: makePrimary,
        position: i,
        fullPath: this.findPathFullName(node.key),
      };
    });

    if (assignments.length > 0 && !assignments.some(a => a.isPrimary)) {
      assignments[0].isPrimary = true;
    }

    this.pathsChange.emit(assignments);

    if (assignments.length > 0) {
      const thePrimary = assignments.find(a => a.isPrimary);
      if (thePrimary) this.primaryChange.emit(thePrimary.pathId);
    } else {
      this.primaryChange.emit('');
    }
  }

  setPrimary(pathId: string) {
    this.primaryChange.emit(pathId);

    // recria array reembalando os state objects pra ngOnPush detectar mudanca no parent se necessario
    const novoArr = this.selectedPaths().map(p => ({
      ...p,
      isPrimary: p.pathId === pathId,
    }));
    this.pathsChange.emit(novoArr);
  }

  removePath(pathId: string) {
    const index = this._selectedNodes.findIndex(n => n.key === pathId);
    if (index >= 0) {
      this._selectedNodes = this._selectedNodes.slice(0, index);
      this.emitSelection();
      this.cdr.markForCheck();
    }
  }

  private findPathFullName(
    id: string,
    nodes: DocumentationPathTreeNode[] = this.treeNodes()
  ): string {
    for (const node of nodes) {
      if (node.key === id) {
        return node.label || id;
      }
      if (node.children && node.children.length > 0) {
        const found = this.findPathFullName(id, node.children);
        if (found !== id) {
          return found;
        }
      }
    }
    return id;
  }
}
